import {
    Component,
    computed,
    DestroyRef,
    inject,
    input,
    OnInit,
    signal,
    viewChild,
} from '@angular/core';
import {
    FormsModule,
    ReactiveFormsModule,
    UntypedFormArray,
    UntypedFormControl,
    Validators,
} from '@angular/forms';
import {
    MatAutocompleteModule,
    MatAutocompleteTrigger,
} from '@angular/material/autocomplete';
import { DepositKeywordsService } from './deposit-keywords.service';
import { DepositForm } from '../../../shared/configurations/deposit-form.constant';
import { ResearchTopic } from '../../../shared/interfaces/research-topic.intreface';
import {
    SelectedResearchTopic,
    TopicType,
} from '../../../shared/interfaces/selected-research-topic.model';
import { DepositFormDataService } from '../../deposit-form-data.service';
import { DepositKeywordsSuggestionsService } from './deposit-keywords-suggestions.service';
import { concat, debounceTime, distinctUntilChanged, finalize } from 'rxjs';
import {
    AriaProgressBarDirective,
    ElementAsHeadingDirective,
    InputExpanderDirective,
    WidthInAnimation,
    WidthOutAnimation,
} from '@exl-ng/mulo-common';
import { ErrorsTranslatePipe } from '../../../shared/pipes/errors-translate.pipe';
import { CustomTranslatePipe } from '../../../shared/pipes/custom-translate.pipe';
import { HighlightFilterPipe } from '../../../shared/pipes/highlight-filter.pipe';
import { TranslateModule } from '@ngx-translate/core';
import { EspMatAutocompleteA11yDirective } from '../../../shared/directives/a11y/mat-autocomplete-extension.directive';
import { MatProgressBar } from '@angular/material/progress-bar';
import { FocusDirective } from '../../../shared/directives/focus/focus.directive';
import { MatInputModule } from '@angular/material/input';
import { MatIcon } from '@angular/material/icon';
import { MatIconButton } from '@angular/material/button';
import { BadgeAutoPopulatedComponent } from '../../../bits/badge/badge-auto-populated/badge-auto-populated.component';
import { MatTooltip } from '@angular/material/tooltip';
import { MatChipsModule } from '@angular/material/chips';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DefaultTranslatePipe } from '../../../shared/pipes/default-translate.pipe';

export type DepositKeywordsMode =
    | 'onlyKeywords'
    | 'onlySubjects'
    | 'keywordsAndSubjects';

@Component({
    selector: 'esp-deposit-keywords',
    templateUrl: './deposit-keywords.component.html',
    styleUrls: ['./deposit-keywords.component.scss'],
    animations: [WidthInAnimation, WidthOutAnimation],
    imports: [
        InputExpanderDirective,
        MatTooltip,
        BadgeAutoPopulatedComponent,
        MatIconButton,
        MatIcon,
        MatInputModule,
        FormsModule,
        MatChipsModule,
        ReactiveFormsModule,
        FocusDirective,
        MatProgressBar,
        AriaProgressBarDirective,
        MatAutocompleteModule,
        EspMatAutocompleteA11yDirective,
        ElementAsHeadingDirective,
        TranslateModule,
        HighlightFilterPipe,
        CustomTranslatePipe,
        ErrorsTranslatePipe,
        DefaultTranslatePipe,
    ],
})
export class DepositKeywordsComponent implements OnInit {
    depositFormDataService = inject(DepositFormDataService);
    readonly chipInput = viewChild('chipInput', {
        read: MatAutocompleteTrigger,
    });
    readonly jump = input(undefined);
    readonly label = input<string>(undefined);
    readonly hint = input<string>(undefined);
    readonly ariaLabel = input('Keywords', { alias: 'aria-label' });
    readonly required = input<boolean>(undefined);
    readonly mode = input<DepositKeywordsMode>('keywordsAndSubjects');
    readonly ctrlArray = input(this.depositFormDataService.topics);
    topics: ResearchTopic[];
    autoCompleteChipList: UntypedFormControl = new UntypedFormControl();
    removable = true;
    noValuesFound = signal(false);
    searchInProgress = signal(false);
    filteredSuggestions: SelectedResearchTopic[];
    private depositKeywordsService = inject(DepositKeywordsService);
    private suggestionsService = inject(DepositKeywordsSuggestionsService);
    private destroyRef = inject(DestroyRef);

    get topicNamesArray() {
        return this.depositFormDataService.mainForm.value.topics.map(
            (subject: ResearchTopic) => {
                return subject.description;
            },
        );
    }

    ngOnInit() {
        this.filteredSuggestions = this.suggestionsService.getSuggestions(
            null,
            this.mode(),
        );
        this.listenToSearchInput();

        if (this.required()) {
            // * This hack provide error for required array controls
            // without it no error will be display if the array is pristine
            this.ctrlArray().addValidators(Validators.required);
            this.ctrlArray().setErrors({ required: true });
        }
    }

    listenToSearchInput() {
        this.autoCompleteChipList.valueChanges
            .pipe(
                debounceTime(DepositForm.SEARCH_DEBOUNCE_TIME),
                distinctUntilChanged(),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe((value) => {
                this.noValuesFound.set(false);
                this.filteredSuggestions =
                    this.suggestionsService.getSuggestions(value, this.mode());
                if (this.mode() === 'onlyKeywords') {
                    this.noDataFound();
                } else if (value?.length >= DepositForm.MIN_INPUT_LENGTH) {
                    this.searchInProgress.set(true);
                    this.getResearchTopics(value);
                }
            });
    }

    getResearchTopics(value) {
        this.depositKeywordsService
            .getResearchTopics(value)
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                finalize(() => {
                    this.searchInProgress.set(false);
                }),
            )
            .subscribe({
                next: (data) => {
                    if (data) {
                        this.topics = data;
                    } else {
                        this.noDataFound();
                    }
                },
                error: () => this.noDataFound(),
            });
    }

    noDataFound() {
        this.noValuesFound.set(true);
        this.topics = [];
    }

    getHierarchyAsString(hierarchy: string[]) {
        return hierarchy.join('/');
    }

    addSuggestionChip(topic: SelectedResearchTopic, input): void {
        const code = topic.code;
        const description = topic.description;
        this.insertChip(code, description, topic.hierarchy, topic.type);
        if (input) {
            input.value = '';
            this.autoCompleteChipList.reset();
        }
    }

    addChip(topic: ResearchTopic, input): void {
        let code = topic.code;
        let description = topic.description;
        if (topic.actualCode) {
            code = topic.actualCode;
            description = topic.actualDescription;
        }
        this.insertChip(
            code,
            description,
            this.getHierarchyAsString(topic.hierarchy),
            TopicType.SUBJECT,
        );
        if (input) {
            input.value = '';
            this.autoCompleteChipList.reset();
        }
    }

    isTopicExists(code, description) {
        for (const topic of this.ctrlArray().value) {
            if (topic.code === code && topic.description === description) {
                return true;
            }
        }
        return false;
    }

    insertChip(code, description, hierarchy, type) {
        this.chipInput().closePanel();
        if (!this.isTopicExists(code, description)) {
            const selectedTopic = new SelectedResearchTopic(
                code,
                description,
                hierarchy,
                type,
            );
            this.depositFormDataService.updateTopics(selectedTopic);
            if (type === TopicType.KEYWORD) {
                this.depositFormDataService.updateKeywords(description);
            } else if (type === TopicType.SUBJECT) {
                this.depositFormDataService.updateSubjects(code);
            }
        }
    }

    handlePaste(event: ClipboardEvent) {
        const pasted = event.clipboardData?.getData('text');

        if (pasted) {
            event.preventDefault();
            this.noValuesFound.set(false);
            this.chipInput().closePanel();

            for (const splitKeyword of pasted.split(';')) {
                const trimKwd = splitKeyword.trim();
                if (trimKwd !== '' && !this.isTopicExists('', trimKwd)) {
                    const topic = new SelectedResearchTopic(
                        '',
                        trimKwd,
                        '',
                        TopicType.KEYWORD,
                    );
                    this.depositFormDataService.updateTopics(topic);
                    this.depositFormDataService.updateKeywords(trimKwd);
                }
            }
            (event.target as HTMLInputElement).value = '';
            this.autoCompleteChipList.reset();
        }
    }

    removeChip(topic: SelectedResearchTopic, index): void {
        this.depositFormDataService.removeTopic(index);
        if (topic.type === TopicType.KEYWORD) {
            this.removeTopicFromArray(
                this.depositFormDataService.keywords,
                topic.description,
                'KEYWORD',
            );
        } else if (topic.type === TopicType.SUBJECT) {
            this.removeTopicFromArray(
                this.depositFormDataService.subjects,
                topic.code,
                'SUBJECT',
            );
        }
    }

    removeTopicFromArray(array: UntypedFormArray, element, flag) {
        const i = array.value.indexOf(element);
        if (i > -1) {
            array.removeAt(i);
        }
        if (flag === 'KEYWORD') {
            this.depositFormDataService.keywordsAutoPopulated.splice(i, 1);
        }
    }

    onInputEnterKey(input: any): void {
        this.noValuesFound.set(false);
        const value = input.value;
        if (value) {
            this.insertChip('', value, '', TopicType.KEYWORD);
            input.value = '';
            this.autoCompleteChipList.reset();
        }
    }

    isAutoPopulated(keyword) {
        return (
            this.depositFormDataService.keywordsAutoPopulated.indexOf(
                keyword.description,
            ) !== -1
        );
    }
}
